import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowRight, TrendingUp, Shield, Zap, Users } from 'lucide-react';
import VideoExplainerSection from '@/components/VideoExplainerSection';

// Karten im "Warum BESS Invest?"-Abschnitt leicht durchscheinend, damit das Hintergrundvideo sichtbar bleibt
const GLASS_CARD = { background: 'rgba(255, 255, 255, 0.74)', backdropFilter: 'blur(2px)' };

const Home = () => {
  const { t, i18n } = useTranslation();
  const [topProjects, setTopProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopProjects();
  }, [i18n.language]);

  const fetchTopProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/top`, { params: { lang: i18n.language } });
      setTopProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch top projects:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fadeIn" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 md:py-32">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxyZW5ld2FibGUlMjBlbmVyZ3l8ZW58MHx8fHwxNzYwMjU5MzA2fDA&ixlib=rb-4.1.0&q=85"
            alt="Hero Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center animate-slideUp">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {t('home.heroTitlePrefix')}
              <span className="block bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                {t('home.heroTitleHighlight')}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/bess/projects">
                <Button size="lg" className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-8" data-testid="explore-projects-btn">
                  {t('home.exploreProjects')}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/bess/info">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900" data-testid="learn-more-btn">
                  {t('home.learnMore')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <VideoExplainerSection testId="why-section" showAgainLabel={t('home.showTextAgain')}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('home.whyTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('home.whySubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card text-center" style={GLASS_CARD} data-testid="feature-returns">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('home.feature1Title')}</h3>
              <p className="text-slate-600">{t('home.feature1Text')}</p>
            </div>

            <div className="card text-center" style={GLASS_CARD} data-testid="feature-security">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('home.feature2Title')}</h3>
              <p className="text-slate-600">{t('home.feature2Text')}</p>
            </div>

            <div className="card text-center" style={GLASS_CARD} data-testid="feature-sustainable">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('home.feature3Title')}</h3>
              <p className="text-slate-600">{t('home.feature3Text')}</p>
            </div>

            <div className="card text-center" style={GLASS_CARD} data-testid="feature-community">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('home.feature4Title')}</h3>
              <p className="text-slate-600">{t('home.feature4Text')}</p>
            </div>
          </div>
        </div>
      </VideoExplainerSection>

      {/* Top Projects Section */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('home.topProjectsTitle')}</h2>
            <p className="text-lg text-slate-600">
              {t('home.topProjectsSubtitle')}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-lg text-slate-600">{t('home.loadingProjects')}</div>
            </div>
          ) : topProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">{t('home.noProjects')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {topProjects.map((project, index) => (
                <div key={project.id} className="project-card" data-testid={`project-card-${index}`}>
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="badge badge-green">{project.status}</span>
                      <span className="text-sm text-slate-500">{project.location}</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{project.title}</h3>
                    <p className="text-slate-600 mb-4 line-clamp-2">{project.description}</p>
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <p className="text-slate-500">{t('home.capacity')}</p>
                        <p className="font-semibold text-slate-900">{project.capacity_mw} MW</p>
                      </div>
                      <div>
                        <p className="text-slate-500">{t('home.returnPa')}</p>
                        <p className="font-semibold text-green-600">{project.expected_return}%</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{t('home.availableShares')}</span>
                        <span className="font-semibold">{project.available_shares}/{project.total_shares}</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-500 to-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${(project.available_shares / project.total_shares) * 100}%` }}
                        />
                      </div>
                    </div>
                    <Link to={`/bess/projects/${project.id}`}>
                      <Button className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white" data-testid={`view-project-btn-${index}`}>
                        {t('home.viewDetails')}
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/bess/projects">
              <Button variant="outline" size="lg" data-testid="view-all-projects-btn">
                {t('home.viewAllProjects')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('home.ctaTitle')}</h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            {t('home.ctaSubtitle')}
          </p>
          <Link to="/bess/register">
            <Button size="lg" className="bg-white text-green-600 hover:bg-slate-100 px-8" data-testid="cta-register-btn">
              {t('home.ctaButton')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
